import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@tempo-io/sui-sonner';
import { useCallback } from 'react';
import { ItemsApi } from '@/common/api/items';
import type {
  BulkFetchV2Response,
  Item,
  ItemCustomFieldValues,
  ItemPatchRequest,
  ItemV2,
} from '@/common/api/types/items';
import { wrapAsDtos } from '@/common/api/types/items';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { FieldValuesApi, type ItemFieldValue } from '@/features/items/api/fieldValues';
import { reconcileFieldValue } from '@/features/items/utils/itemConvert';
import { visualizationQueryKeys } from '@/features/visualizations/queryKeys';

type CustomFieldEntry = { customFieldId: string; value: string | number | boolean | null };
type MultiSelectFieldEntry = {
  customFieldId: string;
  values: (string | number | boolean)[];
  previousEntryIds?: number[];
};
type UpsertManyResult = { fieldId: number; response: ItemFieldValue };

function getMultiFieldValueKey(existingValues: unknown[] | undefined): string | null {
  if (!existingValues?.length) return null;
  const first = existingValues[0];
  if (typeof first !== 'object' || first === null) return null;
  const keys = Object.keys(first).filter((k) => k !== 'id' && k !== 'description');
  return keys[0] ?? null;
}

function buildOptimisticMultiValues(
  values: (string | number | boolean)[],
  existingValues: unknown[] | undefined,
  valueKey: string,
): unknown[] {
  const existingById = new Map<unknown, Record<string, unknown>>();
  if (existingValues) {
    for (const entry of existingValues) {
      if (typeof entry === 'object' && entry !== null && valueKey in entry) {
        existingById.set(
          (entry as Record<string, unknown>)[valueKey],
          entry as Record<string, unknown>,
        );
      }
    }
  }

  return values.map((val, index) => {
    const existing = existingById.get(val);
    if (typeof val === 'object' && val !== null && 'url' in (val as Record<string, unknown>)) {
      const linkVal = val as unknown as { url: string; description?: string };
      const existingByUrl = existingById.get(linkVal.url);
      return {
        id: existingByUrl?.id ?? -(Date.now() + index),
        [valueKey]: linkVal.url,
        description: linkVal.description,
      };
    }
    return {
      id: existing?.id ?? -(Date.now() + index),
      [valueKey]: val,
    };
  });
}

function applyOptimisticUpdateV1(
  item: Item,
  itemData: Partial<ItemPatchRequest>,
  customFieldData: CustomFieldEntry[] | null,
  multiSelectFieldData: MultiSelectFieldEntry[] | null | undefined,
): Item {
  const updatedCustomFields: Record<string, unknown> = { ...item.customFields };
  if (customFieldData) {
    for (const { customFieldId, value } of customFieldData) {
      if (value === 'null' || value === null) {
        delete updatedCustomFields[customFieldId];
        continue;
      }
      updatedCustomFields[customFieldId] = value;
    }
  }
  if (multiSelectFieldData) {
    for (const { customFieldId, values } of multiSelectFieldData) {
      if (values.length === 0) {
        delete updatedCustomFields[customFieldId];
      } else {
        const existing = item.customFields?.[customFieldId];
        const existingArray = Array.isArray(existing) ? existing : undefined;
        const valueKey = getMultiFieldValueKey(existingArray);
        if (valueKey) {
          updatedCustomFields[customFieldId] = buildOptimisticMultiValues(
            values,
            existingArray,
            valueKey,
          );
        } else {
          updatedCustomFields[customFieldId] = values;
        }
      }
    }
  }

  if (customFieldData || multiSelectFieldData) {
    return {
      ...item,
      customFields: updatedCustomFields as ItemCustomFieldValues,
      ...(itemData as Partial<Item>),
    };
  }

  return {
    ...item,
    ...(itemData as Partial<Item>),
  };
}

function applyOptimisticUpdateV2(
  item: Partial<ItemV2>,
  itemData: Partial<ItemPatchRequest>,
  customFieldData: CustomFieldEntry[] | null,
  multiSelectFieldData: MultiSelectFieldEntry[] | null | undefined,
): Partial<ItemV2> {
  const updatedCustomFields: ItemV2['customFieldValues'] = {
    ...(item.customFieldValues ?? {}),
  };
  if (customFieldData) {
    for (const { customFieldId, value } of customFieldData) {
      if (value === 'null' || value === null) {
        delete updatedCustomFields[customFieldId];
        continue;
      }
      updatedCustomFields[customFieldId] = { value };
    }
  }
  if (multiSelectFieldData) {
    for (const { customFieldId, values } of multiSelectFieldData) {
      if (values.length === 0) {
        delete updatedCustomFields[customFieldId];
      } else {
        updatedCustomFields[customFieldId] = wrapAsDtos(values);
      }
    }
  }

  if (customFieldData || multiSelectFieldData) {
    return {
      ...item,
      customFieldValues: updatedCustomFields,
      ...(itemData as Partial<ItemV2>),
    };
  }

  return {
    ...item,
    ...(itemData as Partial<ItemV2>),
  };
}

export const useUpdateVisualizationItemMutation = () => {
  const queryClient = useQueryClient();

  const invalidateItemQueries = useCallback(
    (id: number) => {
      queryClient.invalidateQueries({ queryKey: commonQueryKeys.item(id), refetchType: 'none' });
      queryClient.invalidateQueries({
        queryKey: visualizationQueryKeys.loopItem.detail(id),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: commonQueryKeys.itemsBulkFetch.all(),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: commonQueryKeys.itemsBulkFetchV2.all(),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: commonQueryKeys.plannerTimelineItems.all(),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: visualizationQueryKeys.items.list(),
        refetchType: 'none',
      });
    },
    [queryClient],
  );

  return useMutation({
    mutationFn: async ({
      itemId,
      customFieldData,
      multiSelectFieldData,
      itemData,
    }: {
      itemId: number;
      customFieldData: CustomFieldEntry[] | null;
      multiSelectFieldData?: MultiSelectFieldEntry[] | null;
      itemData: Partial<ItemPatchRequest>;
    }) => {
      const itemUpdate =
        Object.keys(itemData).length > 0
          ? await ItemsApi.updateItem({
              itemId,
              data: itemData,
            })
          : ({ id: itemId } as Item);
      const upsertResults: UpsertManyResult[] = [];
      if (customFieldData) {
        for (const { customFieldId, value } of customFieldData) {
          const fieldId = Number(customFieldId);
          const response = await FieldValuesApi.upsert(itemId, fieldId, value);
          upsertResults.push({ fieldId, response });
        }
      }
      if (multiSelectFieldData) {
        for (const { customFieldId, values, previousEntryIds } of multiSelectFieldData) {
          const fieldId = Number(customFieldId);
          if (previousEntryIds && previousEntryIds.length > 0) {
            await FieldValuesApi.delete(itemId, fieldId, previousEntryIds);
          }
          if (values.length > 0) {
            const response = await FieldValuesApi.upsertMany(itemId, fieldId, values);
            upsertResults.push({ fieldId, response });
          }
        }
      }
      return { itemUpdate, upsertResults };
    },
    onMutate: async ({ itemId, itemData, customFieldData, multiSelectFieldData }) => {
      const loopItemKey = visualizationQueryKeys.loopItem.detail(itemId);
      const itemKey = commonQueryKeys.item(itemId);

      const prevLoopItem = queryClient.getQueryData<Item>(loopItemKey);
      const prevItem = queryClient.getQueryData<Item>(itemKey);
      const prevEntries = queryClient.getQueriesData<BulkFetchV2Response>({
        queryKey: commonQueryKeys.itemsBulkFetchV2.all(),
        exact: false,
      });
      const prevPlannerEntries = queryClient.getQueriesData<{
        pages: BulkFetchV2Response[];
        pageParams: unknown[];
      }>({
        queryKey: commonQueryKeys.plannerTimelineItems.all(),
        exact: false,
      });

      queryClient.setQueryData<Item>(loopItemKey, (oldItem) =>
        oldItem
          ? applyOptimisticUpdateV1(oldItem, itemData, customFieldData, multiSelectFieldData)
          : oldItem,
      );

      // Keep commonQueryKeys.item in sync so other consumers (e.g. items-table)
      // pick up the optimistic update. Fall back to loopItem as the base if not cached.
      const itemBase = prevItem ?? prevLoopItem;
      queryClient.setQueryData<Item>(itemKey, () =>
        itemBase
          ? applyOptimisticUpdateV1(itemBase, itemData, customFieldData, multiSelectFieldData)
          : undefined,
      );

      const itemIdKey = String(itemId);
      queryClient.setQueriesData<BulkFetchV2Response>(
        { queryKey: commonQueryKeys.itemsBulkFetchV2.all(), exact: false },
        (oldData) => {
          if (!oldData?.itemsById[itemIdKey]) return oldData;
          const existingItem = oldData.itemsById[itemIdKey];
          return {
            itemsById: {
              ...oldData.itemsById,
              [itemIdKey]: applyOptimisticUpdateV2(
                existingItem,
                itemData,
                customFieldData,
                multiSelectFieldData,
              ),
            },
          };
        },
      );

      queryClient.setQueriesData<{ pages: BulkFetchV2Response[]; pageParams: unknown[] }>(
        { queryKey: commonQueryKeys.plannerTimelineItems.all(), exact: false },
        (oldData) => {
          if (!oldData) return oldData;
          let touched = false;
          const nextPages = oldData.pages.map((page) => {
            if (!page.itemsById[itemIdKey]) return page;
            touched = true;
            return {
              ...page,
              itemsById: {
                ...page.itemsById,
                [itemIdKey]: applyOptimisticUpdateV2(
                  page.itemsById[itemIdKey],
                  itemData,
                  customFieldData,
                  multiSelectFieldData,
                ),
              },
            };
          });
          if (!touched) return oldData;
          return { ...oldData, pages: nextPages };
        },
      );

      // Cancel after writing so the optimistic value is applied synchronously
      // (no flicker), while still aborting in-flight fetches that could clobber it.
      await queryClient.cancelQueries({ queryKey: commonQueryKeys.itemsBulkFetchV2.all() });
      await queryClient.cancelQueries({ queryKey: commonQueryKeys.plannerTimelineItems.all() });
      await queryClient.cancelQueries({ queryKey: loopItemKey });
      await queryClient.cancelQueries({ queryKey: itemKey });

      return { prevEntries, prevPlannerEntries, prevItem, prevLoopItem, loopItemKey };
    },
    onSuccess: ({ itemUpdate, upsertResults }, variables) => {
      const itemId = itemUpdate.id;
      if (Object.keys(variables.itemData).length > 0) {
        queryClient.setQueryData<Item>(commonQueryKeys.item(itemId), itemUpdate);
      }
      for (const { fieldId, response } of upsertResults) {
        const reconciledValue = reconcileFieldValue(response.fieldType, response.fieldValue);
        const fieldKey = String(fieldId);

        const applyToCustomFields = (
          oldFields: ItemCustomFieldValues | undefined,
        ): ItemCustomFieldValues => {
          const next: ItemCustomFieldValues = { ...(oldFields ?? {}) };
          if (reconciledValue === undefined) {
            delete next[fieldKey];
          } else {
            next[fieldKey] = reconciledValue;
          }
          return next;
        };

        queryClient.setQueryData<Item>(visualizationQueryKeys.loopItem.detail(itemId), (old) =>
          old ? { ...old, customFields: applyToCustomFields(old.customFields) } : old,
        );
        queryClient.setQueryData<Item>(commonQueryKeys.item(itemId), (old) =>
          old ? { ...old, customFields: applyToCustomFields(old.customFields) } : old,
        );
      }
      invalidateItemQueries(itemId);
      // No eager invalidation here: the writes above use `refetchType: 'none'` to avoid refetching per drag frame.
      void reconcileAndSettle(queryClient, [itemId]);
    },
    onError: (err, { itemId }, context) => {
      toast.error(err instanceof Error ? err.message : 'An error occurred while updating the item');
      if (context?.loopItemKey && context.prevLoopItem) {
        queryClient.setQueryData(context.loopItemKey, context.prevLoopItem);
      }
      queryClient.setQueryData(commonQueryKeys.item(itemId), context?.prevItem);
      for (const [key, data] of context?.prevEntries ?? []) {
        queryClient.setQueryData(key, data);
      }
      for (const [key, data] of context?.prevPlannerEntries ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
  });
};
